import { z } from 'zod';
import { isoDateTimeSchema, reasonSchema, uuidSchema, versionSchema, } from '../common/primitives.js';
import { policyRulesSchema } from './policies.js';
export const VOTING_METHODS = ['BINARY', 'MULTIPLE_CHOICE', 'CONSENSUS'];
export const votingMethodSchema = z.enum(VOTING_METHODS);
export const ROUND_STATUSES = ['DRAFT', 'OPEN', 'CLOSED', 'FINALIZED', 'CANCELLED'];
export const roundStatusSchema = z.enum(ROUND_STATUSES);
export const VOTING_OUTCOMES = [
    'APPROVED',
    'REJECTED',
    'OPTION_SELECTED',
    'TIE_NO_DECISION',
    'QUORUM_NOT_REACHED',
];
export const votingOutcomeSchema = z.enum(VOTING_OUTCOMES);
export const FINALIZATION_MODES = ['AUTOMATIC', 'MANUAL', 'EMERGENCY'];
export const finalizationModeSchema = z.enum(FINALIZATION_MODES);
export const VOTE_CHOICES = ['APPROVE', 'REJECT', 'ABSTAIN', 'NEED_DISCUSSION', 'OPTION'];
export const voteChoiceSchema = z.enum(VOTE_CHOICES);
/** Exact decimal string with six fraction digits (weights, sums). */
export const weightSchema = z.string().regex(/^\d+\.\d{6}$/);
export const tallySchema = z.object({
    eligibleCount: z.number().int(),
    eligibleWeight: weightSchema,
    votedCount: z.number().int(),
    /** Members counted toward quorum (abstentions only when the policy says so). */
    participatingCount: z.number().int(),
    participatingWeight: weightSchema,
    /** Base for "of cast" thresholds: approve + reject + need-discussion (+ option votes). */
    castWeight: weightSchema,
    approveWeight: weightSchema,
    rejectWeight: weightSchema,
    abstainWeight: weightSchema,
    /** Part of `abstainWeight` produced by `ABSTAIN_AFTER_DEADLINE` (never shown as a cast vote). */
    implicitAbstainWeight: weightSchema,
    needDiscussionWeight: weightSchema,
    noResponseCount: z.number().int(),
    optionWeights: z.array(z.object({ optionId: uuidSchema, weight: weightSchema })),
    quorumMet: z.boolean(),
    thresholdMet: z.boolean(),
    /** Outcome if the round were evaluated now. */
    outcome: votingOutcomeSchema,
    selectedOptionId: uuidSchema.nullable(),
    /** True when no remaining or changed vote can alter `outcome` under the policy. */
    outcomeCertain: z.boolean(),
    /** Plain-language trace of how the rule was applied. */
    explanation: z.array(z.string()),
});
export const roundPolicySchema = z.object({
    policyId: uuidSchema,
    policyKey: z.string(),
    policyName: z.string(),
    versionId: uuidSchema,
    versionNumber: z.number().int(),
    rules: policyRulesSchema,
    /** Human-readable rule lines shown before voting (spec §7.4). */
    summary: z.array(z.string()),
});
export const eligibleVoterSchema = z.object({
    memberId: uuidSchema,
    displayName: z.string(),
    weight: weightSchema,
    isRequired: z.boolean(),
    hasVoted: z.boolean(),
    /** `null` while votes are hidden until close. */
    choice: voteChoiceSchema.nullable(),
    optionId: uuidSchema.nullable(),
    revision: z.number().int().nullable(),
    castAt: isoDateTimeSchema.nullable(),
});
export const myVoteSchema = z.object({
    choice: voteChoiceSchema,
    optionId: uuidSchema.nullable(),
    comment: z.string().nullable(),
    revision: z.number().int(),
    castAt: isoDateTimeSchema,
});
export const votingRoundSchema = z.object({
    id: uuidSchema,
    topicId: uuidSchema,
    roundNumber: z.number().int(),
    method: votingMethodSchema,
    status: roundStatusSchema,
    outcome: votingOutcomeSchema.nullable(),
    selectedOptionId: uuidSchema.nullable(),
    policy: roundPolicySchema,
    hiddenUntilClose: z.boolean(),
    opensAt: isoDateTimeSchema.nullable(),
    deadlineAt: isoDateTimeSchema.nullable(),
    closedAt: isoDateTimeSchema.nullable(),
    finalizedAt: isoDateTimeSchema.nullable(),
    finalizedBy: z.string().nullable(),
    finalizationMode: finalizationModeSchema.nullable(),
    cancelledAt: isoDateTimeSchema.nullable(),
    cancelReason: z.string().nullable(),
    createdBy: z.string(),
    createdAt: isoDateTimeSchema,
    ballot: z.array(z.object({ optionId: uuidSchema, title: z.string(), position: z.number().int() })),
    eligibility: z.array(eligibleVoterSchema),
    /** `null` while votes are hidden until close. Final rounds show the recorded result. */
    tally: tallySchema.nullable(),
    myVote: myVoteSchema.nullable(),
    iAmEligible: z.boolean(),
    canVote: z.boolean(),
    canManage: z.boolean(),
    canFinalize: z.boolean(),
    decisionId: uuidSchema.nullable(),
    version: versionSchema,
});
export const votingRoundListSchema = z.object({ items: z.array(votingRoundSchema) });
export const createRoundRequestSchema = z
    .object({
    method: votingMethodSchema,
    /** Ignored when the topic category is mapped to a policy (the mapping wins). */
    policyId: uuidSchema.nullable().optional(),
    optionIds: z.array(uuidSchema).max(20).default([]),
    deadlineAt: isoDateTimeSchema.nullable(),
    /** Sensitive cases: hide individual votes until close (spec §7.4). */
    hiddenUntilClose: z.boolean().default(false),
    /** Open immediately (snapshots eligibility now) instead of leaving a DRAFT. */
    openNow: z.boolean().default(true),
})
    .refine((value) => value.method !== 'MULTIPLE_CHOICE' || value.optionIds.length >= 2, {
    message: 'Pick at least two options for a multiple-choice vote',
    path: ['optionIds'],
})
    .refine((value) => value.method === 'MULTIPLE_CHOICE' || value.optionIds.length === 0, {
    message: 'Only multiple-choice votes have a ballot of options',
    path: ['optionIds'],
})
    .refine((value) => new Set(value.optionIds).size === value.optionIds.length, {
    message: 'Each option can appear once',
    path: ['optionIds'],
});
export const openRoundRequestSchema = z.object({ version: versionSchema });
export const extendRoundRequestSchema = z.object({
    version: versionSchema,
    deadlineAt: isoDateTimeSchema,
    reason: reasonSchema,
});
export const cancelRoundRequestSchema = z.object({ version: versionSchema, reason: reasonSchema });
export const finalizeRoundRequestSchema = z.object({ version: versionSchema });
/** Header carrying the client-generated idempotency key for votes and other binding actions. */
export const IDEMPOTENCY_KEY_HEADER = 'idempotency-key';
export const idempotencyKeySchema = z
    .string()
    .regex(/^[A-Za-z0-9_-]{16,200}$/, 'Idempotency-Key must be 16–200 URL-safe characters');
export const castVoteRequestSchema = z
    .object({
    choice: voteChoiceSchema,
    optionId: uuidSchema.nullable().optional(),
    comment: z.string().trim().max(2000).nullable().optional(),
})
    .refine((value) => (value.choice === 'OPTION') === ((value.optionId ?? null) !== null), {
    message: 'Pick an option for an option vote (and only then)',
    path: ['optionId'],
});
//# sourceMappingURL=voting.js.map